/* eslint-disable max-lines */
import { faPaintBrush } from '@fortawesome/pro-light-svg-icons'
import { faTablePivot } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import {
  type ChartAggregation,
  generateLabelSqlFieldName,
  type PieChartAggregation,
  type ViewUiKpi,
} from '@salescore/core'
import type {
  ChartLabelDisplayModeValue,
  ChartLegendsDisplayModeValue,
  StackedChartType,
} from '@salescore/frontend-common'
import { Button, Checkbox, Row, Select, Space } from 'antd'
import { Array, Order, pipe, Record, String } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useEffect } from 'react'
import { useRecoilState } from 'recoil'

import { useComboChartAggregation } from '../../../hooks/useComboChartAggregation'
import { usePieChartAggregation } from '../../../hooks/usePieChartAggregation'
import { aggregationToRows, type KpiChartDisplaySettingPresetsState } from '../../../kpi/kpiChartDisplaySettingPresets'
import type { KpiPivotConfig } from '../../../recoil/context'
import { kpiParameterAtom } from '../../../recoil/navigation/atoms'
import { Section } from '../Section'
import type {
  ChartDesignSettings,
  KpiDisplayFormat,
  KpiPieChartCategoryFilters,
  SetChartAggregation,
  SetChartDesignSettings,
  SetKpiPieChartCategoryFilters,
} from '../ViewUIKpi'
import { DimensionPicker } from '../ViewUIKpiPivot/DimensionPicker'
import { DimensionsPicker } from '../ViewUIKpiPivot/DimensionsPicker'
import { DECIMALPLACE_TYPES, UNIT_TYPES } from '../ViewUIKpiPivot/KpiForm/KpiFormDetailFormItems'
import { useRecords } from './KpiStackedChart'

const stackedChartTypeOptions: Array<{ label: string; value: StackedChartType }> = [
  { label: t(`棒グラフ`), value: 'bar' },
  { label: t(`折れ線グラフ`), value: 'line' },
  { label: t(`エリアグラフ`), value: 'area' },
]
export const labelDisplayOptions: Array<{ label: string; value: ChartLabelDisplayModeValue }> = [
  { label: t(`自動`), value: 'auto' },
  { label: t(`全て表示する`), value: 'showAll' },
  { label: t(`表示しない`), value: 'none' },
]
export const labelUnitOptions = UNIT_TYPES
export const labelDecimalPlaceOptions = DECIMALPLACE_TYPES
const legendsDisplayOptions: Array<{ label: string; value: ChartLegendsDisplayModeValue }> = [
  { label: t(`自動`), value: 'auto' },
  { label: t(`表示する`), value: 'show' },
  { label: t(`表示しない`), value: 'none' },
]

export const KpiParameterSider = ({
  component,
  format,
  close,
  kpiPieChartCategoryFilters,
  setKpiPieChartCategoryFilters,
  setChartDesignSettings,
  kpiChartDisplaySettingPresetsState,
  setChartAggregation,
  view,
}: {
  component: ViewUiKpi
  format?: KpiDisplayFormat
  close?: () => void
  kpiPieChartCategoryFilters: KpiPieChartCategoryFilters
  setKpiPieChartCategoryFilters: SetKpiPieChartCategoryFilters
  setChartDesignSettings: SetChartDesignSettings
  kpiChartDisplaySettingPresetsState: KpiChartDisplaySettingPresetsState
  setChartAggregation: SetChartAggregation
  view: ViewFieldsFragment
}): ReactElement => (
  <div className="h-full overflow-y-auto bg-white p-4" style={{ borderRight: `1px solid ${CONSTANT.colors.border}` }}>
    <Row justify="space-between">
      <Space className="mb-3 text-lg font-bold">
        <FontAwesomeIcon icon={faTablePivot} />
        {t(`集計設定`)}
      </Space>
      {close !== undefined && (
        <Space>
          <Button
            onClick={() => {
              close()
            }}
            type="text"
          >
            {t(`閉じる`)}
          </Button>
        </Space>
      )}
    </Row>
    <DimensionPickerWrapper
      component={component}
      format={format}
      kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
      setKpiPieChartCategoryFilters={setKpiPieChartCategoryFilters}
      setChartDesignSettings={setChartDesignSettings}
      kpiChartDisplaySettingPresetsState={kpiChartDisplaySettingPresetsState}
      setChartAggregation={setChartAggregation}
      view={view}
    />
  </div>
)

const DimensionPickerWrapper = ({
  component,
  format,
  kpiPieChartCategoryFilters,
  setKpiPieChartCategoryFilters,
  setChartDesignSettings,
  kpiChartDisplaySettingPresetsState,
  setChartAggregation,
  view,
}: {
  component: ViewUiKpi
  format: KpiDisplayFormat | undefined
  kpiPieChartCategoryFilters: KpiPieChartCategoryFilters
  setKpiPieChartCategoryFilters: SetKpiPieChartCategoryFilters
  setChartDesignSettings: SetChartDesignSettings
  kpiChartDisplaySettingPresetsState: KpiChartDisplaySettingPresetsState
  setChartAggregation: SetChartAggregation
  view: ViewFieldsFragment
}): ReactElement => {
  if (format === 'table') {
    // 行列を選ばせる基本的なUI
    return (
      <>
        <Section
          title={t(`集計項目 - 行`)}
          description={t(`集計したい項目をクリックしてください。並び替えもできます。`)}
        >
          <DimensionsPicker dimensions={component.dimensions} type={'rows'} />
        </Section>
        <Section
          title={t(`集計項目 - 列`)}
          description={t(`集計したい項目をクリックしてください。並び替えもできます。`)}
        >
          <DimensionsPicker dimensions={component.dimensions} type={'columns'} />
        </Section>
      </>
    )
  }

  if (format === 'pie') {
    const chartDesignSettings = kpiChartDisplaySettingPresetsState.pie.design
    return (
      <>
        <PieChartDimensionsPicker
          component={component}
          kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
          setKpiPieChartCategoryFilters={setKpiPieChartCategoryFilters}
          initialAggregation={kpiChartDisplaySettingPresetsState.pie.aggregation}
          setChartAggregation={setChartAggregation}
          view={view}
        />
        <DesignConfigPart
          format={format}
          chartDesignSettings={chartDesignSettings}
          setChartDesignSettings={setChartDesignSettings}
        />
      </>
    )
  }

  if (format === 'combo') {
    const chartDesignSettings = kpiChartDisplaySettingPresetsState.combo.design
    return (
      <>
        <BarChartDimensionsPicker
          component={component}
          chartDesignSettings={chartDesignSettings}
          setChartDesignSettings={setChartDesignSettings}
          initialAggregation={kpiChartDisplaySettingPresetsState.combo.aggregation}
          setChartAggregation={setChartAggregation}
        />
        <DesignConfigPart
          format={format}
          chartDesignSettings={chartDesignSettings}
          setChartDesignSettings={setChartDesignSettings}
        />
      </>
    )
  }

  // 行列を選ばせる基本的なUI
  return (
    <>
      <Section title={t(`集計項目 - 行`)} description={t(`集計したい項目をクリックしてください。並び替えもできます。`)}>
        <DimensionsPicker dimensions={component.dimensions} type={'rows'} />
      </Section>
      <Section title={t(`集計項目 - 列`)} description={t(`集計したい項目をクリックしてください。並び替えもできます。`)}>
        <DimensionsPicker dimensions={component.dimensions} type={'columns'} />
      </Section>
    </>
  )
}

const PieChartDimensionsPicker = ({
  component,
  kpiPieChartCategoryFilters,
  setKpiPieChartCategoryFilters,
  initialAggregation,
  setChartAggregation,
  view,
}: {
  component: ViewUiKpi
  kpiPieChartCategoryFilters: KpiPieChartCategoryFilters
  setKpiPieChartCategoryFilters: SetKpiPieChartCategoryFilters
  initialAggregation: ChartAggregation
  setChartAggregation: SetChartAggregation
  view: ViewFieldsFragment
}): ReactElement => {
  const { aggregation, setBreakdown, setCategory } = usePieChartAggregation()
  const [, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)

  // 初回マウント時のみ、保存済みプリセットの集計軸をkpiParameterAtomに同期する。
  // ユーザー操作時はsetKpiPivotParameterを直接呼ぶため、initialAggregationの変更監視は不要。
  useEffect(() => {
    const rows = aggregationToRows(initialAggregation)
    if (rows.length === 0) return
    setKpiPivotParameter((oldValue) => {
      if (JSON.stringify(oldValue.pivot.rows) === JSON.stringify(rows)) return oldValue
      return { ...oldValue, pivot: { rows, columns: [] } }
    })
  }, [])

  return (
    <>
      <Section title={t(`内訳(必須)`)} description={t(`内訳にしたい項目を選択してください`)}>
        <DimensionPicker
          dimensions={component.dimensions}
          currentDimension={aggregation?.breakdown}
          onChange={(dimension) => {
            setBreakdown(dimension)
            if (dimension !== undefined) {
              setChartAggregation({ chartFormat: 'pie', breakdown: dimension, category: aggregation?.category })
            }
          }}
        />
      </Section>
      <Section title={t(`カテゴリ`)} description={t(`カテゴリにしたい項目を選択してください`)}>
        <DimensionPicker
          allowClear
          dimensions={component.dimensions}
          currentDimension={aggregation?.category}
          onChange={(dimension) => {
            setCategory(dimension)
            if (aggregation?.breakdown !== undefined) {
              setChartAggregation({ chartFormat: 'pie', breakdown: aggregation.breakdown, category: dimension })
            }
          }}
        />
      </Section>
      <PieChartCategoryFiltersPicker
        pivot={component.pivot}
        aggregation={aggregation}
        kpiPieChartCategoryFilters={kpiPieChartCategoryFilters}
        setKpiPieChartCategoryFilters={setKpiPieChartCategoryFilters}
        view={view}
      />
    </>
  )
}

const PieChartCategoryFiltersPicker = ({
  pivot,
  aggregation,
  kpiPieChartCategoryFilters,
  setKpiPieChartCategoryFilters,
  view,
}: {
  pivot: KpiPivotConfig
  aggregation: PieChartAggregation | undefined
  kpiPieChartCategoryFilters: KpiPieChartCategoryFilters
  setKpiPieChartCategoryFilters: SetKpiPieChartCategoryFilters
  view: ViewFieldsFragment
}): ReactElement => {
  const { records, key2 } = useRecords({ pivot, view })
  const dimensionAsCategory = aggregation?.category
  const filterOptions = pipe(
    records,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    Array.groupBy((x) => x[key2] as string),
    Record.toEntries,
    Array.map(([key, records]) => {
      const firstRecord = records[0]
      return {
        key,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
        label: (firstRecord[generateLabelSqlFieldName(key2)] as string) ?? (firstRecord[key2] as string),
      }
    }),
    Array.sortBy(
      Order.mapInput(
        Order.make<string>((a, b) => String.localeCompare(b)(a)),
        (x) => x.label,
      ),
    ),
  )

  useEffect(() => {
    // カテゴリの選択解除タイミングでフィルターをリセットしてしまうと、
    // その後recordsが変更されたタイミングと合わせてグラフが2回描画されてしまうため、
    // recordsの変更タイミングに合わせてフィルターをリセットしている
    setKpiPieChartCategoryFilters((oldValue) =>
      // eslint-disable-next-line max-nested-callbacks
      oldValue.filter((x) => filterOptions.some((option) => option.key === x.key)),
    )
  }, [records])

  if (dimensionAsCategory === undefined) {
    return <></>
  }

  return (
    <Section title={t(`カテゴリのフィルター`)}>
      <div className="flex w-full">
        <Select
          className="max-w-xs grow-[7]"
          mode="multiple"
          allowClear
          options={filterOptions.map((option) => ({
            value: option.key,
            label: <span className="whitespace-pre-wrap break-words">{option.label}</span>,
          }))}
          value={kpiPieChartCategoryFilters.map((x) => x.key)}
          onChange={(values) => {
            setKpiPieChartCategoryFilters(values.map((key) => ({ key })))
          }}
        />
      </div>
    </Section>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function BarChartDimensionsPicker({
  component,
  chartDesignSettings,
  setChartDesignSettings,
  initialAggregation,
  setChartAggregation,
}: {
  component: ViewUiKpi
  chartDesignSettings: ChartDesignSettings
  setChartDesignSettings: SetChartDesignSettings
  initialAggregation: ChartAggregation
  setChartAggregation: SetChartAggregation
}): ReactElement {
  const { aggregation, setXAxis, setBreakdown } = useComboChartAggregation()
  const [, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)

  // 初回マウント時のみ、保存済みプリセットの集計軸をkpiParameterAtomに同期する。
  // ユーザー操作時はsetKpiPivotParameterを直接呼ぶため、initialAggregationの変更監視は不要。
  useEffect(() => {
    const rows = aggregationToRows(initialAggregation)
    if (rows.length === 0) return
    setKpiPivotParameter((oldValue) => {
      if (JSON.stringify(oldValue.pivot.rows) === JSON.stringify(rows)) return oldValue
      return { ...oldValue, pivot: { rows, columns: [] } }
    })
  }, [])

  return (
    <>
      <Section title={t(`X軸(必須)`)} description={t(`X軸にしたい項目を選択してください`)}>
        <DimensionPicker
          dimensions={component.dimensions}
          currentDimension={aggregation?.xAxis}
          onChange={(dimension) => {
            setXAxis(dimension)
            if (dimension !== undefined) {
              setChartAggregation({ chartFormat: 'combo', xAxis: dimension, breakdown: aggregation?.breakdown })
            }
          }}
        />
      </Section>
      <Section title={t(`内訳`)} description={t(`内訳にしたい項目を選択してください`)}>
        <Space className="w-full" direction="vertical">
          <DimensionPicker
            allowClear
            dimensions={component.dimensions}
            currentDimension={aggregation?.breakdown}
            onChange={(dimension) => {
              setBreakdown(dimension)
              if (aggregation?.xAxis !== undefined) {
                setChartAggregation({ chartFormat: 'combo', xAxis: aggregation.xAxis, breakdown: dimension })
              }

              if (dimension === undefined) {
                setChartDesignSettings((oldValue) => ({
                  ...oldValue,
                  chartValueLabelShouldShowPercentage: false,
                }))
              }
            }}
          />
          {aggregation?.breakdown !== undefined && (
            <Checkbox
              checked={chartDesignSettings.chartValueLabelShouldShowPercentage}
              onChange={(event) => {
                setChartDesignSettings((oldValue) => ({
                  ...oldValue,
                  chartValueLabelShouldShowPercentage: event.target.checked,
                }))
              }}
            >
              {t('パーセンテージを表示')}
            </Checkbox>
          )}
        </Space>
      </Section>
    </>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function DesignConfigPart({
  format,
  chartDesignSettings,
  setChartDesignSettings,
}: {
  format: KpiDisplayFormat
  chartDesignSettings: ChartDesignSettings
  setChartDesignSettings: SetChartDesignSettings
}): ReactElement {
  return (
    <>
      <Space className="mb-3 text-lg font-bold">
        <FontAwesomeIcon icon={faPaintBrush} />
        {t(`デザイン設定`)}
      </Space>
      {format !== 'pie' && (
        <>
          <Section title={t('グラフの種類')} isSmall={true}>
            <div className="flex w-full">
              <Select
                className="max-w-xs grow-[7]"
                options={stackedChartTypeOptions}
                value={chartDesignSettings.chartType}
                onChange={(value) => {
                  setChartDesignSettings((oldValue) => ({
                    ...oldValue,
                    chartType: value,
                  }))
                }}
              />
            </div>
          </Section>
          <Section title={t(`X軸のラベル表示`)} isSmall={true}>
            <div className="flex w-full">
              <Select
                className="max-w-xs grow-[7]"
                options={labelDisplayOptions}
                value={chartDesignSettings.chartAxisLabelDisplayMode}
                onChange={(value) => {
                  setChartDesignSettings((oldValue) => ({
                    ...oldValue,
                    chartAxisLabelDisplayMode: value,
                  }))
                }}
              />
            </div>
          </Section>
        </>
      )}
      <Section title={t(`値ラベルの表示`)} isSmall={true}>
        <div className="flex w-full">
          <Select
            className="max-w-xs grow-[7]"
            options={labelDisplayOptions}
            value={chartDesignSettings.chartValueLabelDisplayMode}
            onChange={(value) => {
              setChartDesignSettings((oldValue) => ({
                ...oldValue,
                chartValueLabelDisplayMode: value,
              }))
            }}
          />
        </div>
      </Section>
      <Section title={t(`値ラベルの単位`)} isSmall={true}>
        <div className="flex w-full">
          <Select
            className="max-w-xs grow-[7]"
            options={labelUnitOptions}
            value={chartDesignSettings.chartValueLabelUnitType}
            onChange={(value) => {
              setChartDesignSettings((oldValue) => ({
                ...oldValue,
                chartValueLabelUnitType: value,
              }))
            }}
          />
        </div>
      </Section>
      <Section title={t(`値ラベルの桁`)} isSmall={true}>
        <div className="flex w-full">
          <Select
            className="max-w-xs grow-[7]"
            options={labelDecimalPlaceOptions}
            value={chartDesignSettings.chartValueLabelDecimalPlaceType}
            onChange={(value) => {
              setChartDesignSettings((oldValue) => ({
                ...oldValue,
                chartValueLabelDecimalPlaceType: value,
              }))
            }}
          />
        </div>
      </Section>
      <Section title={t(`凡例の表示`)} isSmall={true}>
        <div className="flex w-full">
          <Select
            className="max-w-xs grow-[7]"
            options={legendsDisplayOptions}
            value={chartDesignSettings.chartLegendsDisplayMode}
            onChange={(value) => {
              setChartDesignSettings((oldValue) => ({
                ...oldValue,
                chartLegendsDisplayMode: value,
              }))
            }}
          />
        </div>
      </Section>
    </>
  )
}
